import { invoke } from '~/lib/tauriInvoke'
import type {
  Board,
  BoardAccount,
  BoardAssigneeOption,
  BoardCard,
  BoardCardDetails,
  BoardComment,
  BoardMapping,
  BoardProbeResult,
  BoardProvider,
  BoardServerArgs,
  ConnectBoardArgs,
  ExternalBoard,
  MoveBoardCardArgs,
  UpdateBoardCardArgs,
} from '~/types/boards'

// Thin invoke wrappers over src-tauri/src/commands/boards.rs (one per BoardsApi rpc). State (boards, cards,
// optimistic moves) lives in stores/boards.ts; components that only need a one-off call (the connect wizard,
// the settings «Интеграции» section) use these directly.
export function useBoards() {
  return {
    // --- Connection wizard ---
    probeBoardServer: (args: BoardServerArgs) =>
      invoke<BoardProbeResult>('probe_board_server', { payload: args }),
    verifyBoardToken: (args: BoardServerArgs) =>
      invoke<BoardProbeResult>('verify_board_token', { payload: args }),
    listExternalBoards: (args: BoardServerArgs) =>
      invoke<ExternalBoard[]>('list_external_boards', { payload: args }),
    connectBoard: (args: ConnectBoardArgs) =>
      invoke<Board>('connect_board', { payload: args }),
    updateBoardMapping: (channelId: string, mapping: BoardMapping | null, sprintId?: string | null) =>
      invoke<Board>('update_board_mapping', {
        payload: { channel_id: channelId, mapping, sprint_id: sprintId ?? null },
      }),
    disconnectBoard: (channelId: string) => invoke<void>('disconnect_board', { channelId }),

    // --- Board ---
    getBoard: (channelId: string) => invoke<Board>('get_board', { channelId }),
    listBoardCards: (channelId: string, options: { query?: string, sprintId?: string, cardIds?: string[] } = {}) =>
      invoke<BoardCard[]>('list_board_cards', {
        payload: {
          channel_id: channelId,
          query: options.query ?? '',
          sprint_id: options.sprintId ?? '',
          card_ids: options.cardIds ?? [],
        },
      }),
    getBoardCard: (channelId: string, cardId: string) =>
      invoke<BoardCardDetails>('get_board_card', { channelId, cardId }),
    watchBoard: (channelId: string) => invoke<void>('watch_board', { channelId }),

    // --- Card changes ---
    createBoardCard: (channelId: string, title: string, columnId = '', fields: Record<string, string> = {}, description = '') =>
      invoke<BoardCard>('create_board_card', {
        payload: { channel_id: channelId, title, description, column_id: columnId, project: '', fields },
      }),
    updateBoardCard: (args: UpdateBoardCardArgs) => invoke<BoardCard>('update_board_card', { payload: args }),
    moveBoardCard: (args: MoveBoardCardArgs) => invoke<BoardCard>('move_board_card', { payload: args }),
    setBoardCardAssignees: (channelId: string, cardId: string, externalUserIds: string[], ioptaIds: string[]) =>
      invoke<BoardCard>('set_board_card_assignees', {
        payload: { channel_id: channelId, card_id: cardId, external_user_ids: externalUserIds, iopta_ids: ioptaIds },
      }),
    commentBoardCard: (channelId: string, cardId: string, text: string) =>
      invoke<BoardComment>('comment_board_card', { channelId, cardId, text }),

    // --- Assignees and own accounts ---
    listBoardAssignees: (channelId: string, query = '') =>
      invoke<BoardAssigneeOption[]>('list_board_assignees', { channelId, query }),
    linkBoardAccount: (provider: BoardProvider, baseUrl: string, token: string) =>
      invoke<BoardAccount>('link_board_account', { provider, baseUrl, token }),
    unlinkBoardAccount: (provider: BoardProvider, baseUrl: string) =>
      invoke<void>('unlink_board_account', { provider, baseUrl }),
    listMyBoardAccounts: () => invoke<BoardAccount[]>('list_my_board_accounts'),
  }
}
